import React, { useState, useEffect, useCallback } from 'react';
import { Clock, RefreshCw } from 'lucide-react';
import { claimsService, LeadClaim } from '@/services/claimsService';
import { useUser } from '@/contexts/UserContext';
import { showToast } from '@/components/ui/Toast';
import ClaimsTable from '../_Components/ClaimsTable';

const PendingClaims: React.FC = () => {
  const { userData } = useUser();
  const isSuperAdmin = userData?.data?.role?.role_id === 9401;

  const [claims, setClaims] = useState<LeadClaim[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});
  const [heldClaims, setHeldClaims] = useState<Set<string>>(new Set());

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await claimsService.getPendingClaims();
      if (res.success) setClaims(res.data || []);
    } catch {
      showToast('Failed to fetch pending claims', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  const handleApprove = async (uuid: string) => {
    setActionLoading(uuid + '_approve');
    try {
      const override = heldClaims.has(uuid);
      const res: any = await claimsService.approve(uuid, adminNotes[uuid] || '', override);
      if (res.success) {
        showToast('Claim approved — lead reassigned', 'success');
        setAdminNotes(prev => { const n = { ...prev }; delete n[uuid]; return n; });
        setHeldClaims(prev => { const n = new Set(prev); n.delete(uuid); return n; });
        fetchClaims();
      } else if (res.status === 'held' || res.data?.held) {
        // The current owner has worked this customer: say who and when, and make
        // the next click a deliberate "Approve anyway".
        setHeldClaims(prev => new Set(prev).add(uuid));
        showToast(res.message, 'warning');
      } else {
        showToast(res.message || 'Failed to approve', 'error');
      }
    } catch { showToast('Failed to approve', 'error'); }
    finally { setActionLoading(null); }
  };

  const handleDecline = async (uuid: string) => {
    setActionLoading(uuid + '_decline');
    try {
      const res: any = await claimsService.decline(uuid, adminNotes[uuid] || '');
      if (res.success) {
        showToast('Claim declined', 'success');
        setAdminNotes(prev => { const n = { ...prev }; delete n[uuid]; return n; });
        fetchClaims();
      } else {
        showToast(res.message || 'Failed to decline', 'error');
      }
    } catch { showToast('Failed to decline', 'error'); }
    finally { setActionLoading(null); }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Fixed header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5 text-brandColor" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Pending Requests</h1>
            <p className="text-xs text-gray-400">Review and action agent claim requests</p>
          </div>
          {claims.length > 0 && (
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
              {claims.length} pending
            </span>
          )}
        </div>
        <button
          onClick={fetchClaims}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-2 border-brandColor border-t-transparent rounded-full" />
          </div>
        ) : (
          <ClaimsTable
            claims={claims}
            mode="pending"
            isSuperAdmin={isSuperAdmin}
            actionLoading={actionLoading}
            adminNotes={adminNotes}
            onApprove={handleApprove}
            onDecline={handleDecline}
            heldClaims={heldClaims}
            onNoteChange={(uuid, note) => setAdminNotes(prev => ({ ...prev, [uuid]: note }))}
          />
        )}
      </div>
      </div>
    </div>
  );
};

export default PendingClaims;
